import i18n from "i18next";
import { DefaultValues } from "react-hook-form";
import { CustomerOrderChange } from "src/api/generated/erp/db/types/tables/customerOrderChange.ts";
import { CustomerOrderChangeApi } from "src/api/generated/erp/sales/customerOrder/api/customerOrderChangeApi.ts";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import { FormRichTextEditor } from "src/components/common/forms/fields/FormRichTextEditor.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { requireRule } from "src/components/common/forms/validation.ts";
import { CustomerOrderChangeObjectType } from "../../../../../api/generated/erp/db/types/enums/customerOrderChangeObjectType.ts";
import { AavoForm } from "../../../../common/forms/AavoForm.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CustomerOrderChangeFormProps extends FormCommonProps<number> {
	existingCustomerOrderChange: CustomerOrderChange | undefined;
	objectType: CustomerOrderChangeObjectType;
	objectId: number;
	defaultValueOverrides?: Partial<CustomerOrderChange>;
	subjectOnly?: boolean;
	/** Lets the content be written although only the subject is otherwise editable, when nothing generated it. */
	editableContent?: boolean;
	/**
	 * Replaces the default insert or update. Returns the ID of the saved order change.
	 */
	submitOverride?: (values: CustomerOrderChange) => Promise<number>;
}

export const CustomerOrderChangeForm = ({
	existingCustomerOrderChange,
	objectType,
	objectId,
	defaultValueOverrides,
	subjectOnly = false,
	editableContent = false,
	submitOverride,
	onCompleted,
	onFormEdited,
}: CustomerOrderChangeFormProps) => {
	return (
		<AavoForm<CustomerOrderChange, number>
			defaultValues={getDefaultValues()}
			submit={submit}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			columns={1}
			agentContext={{
				key: defineAgentUiContextKey("erp.sales.customerOrderChange.form"),
				title: i18n.t("order_change"),
				data: {
					customerOrderChangeId: existingCustomerOrderChange?.customerOrderChangeId ?? null,
					objectType,
					objectId,
				},
			}}
			render={({ control }) => (
				<>
					<FormTextField control={control} name="subject" label={i18n.t("subject")} rules={requireRule()} />
					<FormRichTextEditor
						control={control}
						name="content"
						label={i18n.t("content")}
						readOnly={subjectOnly && !editableContent}
					/>
					<FormNumberField
						control={control}
						name="costChange"
						label={i18n.t("cost_change")}
						disabled={subjectOnly}
					/>
					<FormNumberField
						control={control}
						name="priceChange"
						label={i18n.t("price_change")}
						disabled={subjectOnly}
					/>
					<FormCheckbox
						control={control}
						name="printOnOrderConfirmation"
						label={i18n.t("print_on_order_confirmation")}
						disabled={subjectOnly}
					/>
				</>
			)}
		/>
	);

	function getDefaultValues(): DefaultValues<CustomerOrderChange> {
		if (existingCustomerOrderChange != null) {
			return existingCustomerOrderChange;
		} else {
			return {
				objectType,
				objectId,
				costChange: 0,
				priceChange: 0,
				printOnOrderConfirmation: false,
				subject: "",
				content: "",
				...defaultValueOverrides,
			};
		}
	}

	async function submit(values: CustomerOrderChange) {
		if (submitOverride) {
			return await submitOverride(values);
		} else if (existingCustomerOrderChange) {
			if (existingCustomerOrderChange.objectType === "CUSTOMER_OFFER") {
				await CustomerOrderChangeApi.updateCustomerOfferOrderChange({
					customerOrderChange: values,
				});
			} else {
				await CustomerOrderChangeApi.update({
					customerOrderChange: values,
				});
			}
			return existingCustomerOrderChange.customerOrderChangeId;
		} else {
			return await CustomerOrderChangeApi.insert({
				customerOrderChange: values,
			});
		}
	}
};
